import React, { useEffect, useMemo, useRef, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, ComboboxItem, Input, Radio, Tooltip } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useCountryStatesQuery } from 'hooks/queries/Customers/useCountryStates'
import { useIndustryOptions } from 'hooks/queries/Customers/useIndustryOptions'
import { useMonthlyVolumesQuery } from 'hooks/queries/Customers/useMonthlyVolumesQuery'
import { useAllowedCountriesQuery } from 'hooks/queries/useAllowedCountriesQuery'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { setFieldErrors } from 'pages/EmbeddedCustomers/helpers/form'
import { scrollToTopError } from 'pages/EmbeddedCustomers/helpers/utils'
import { addCustomerDetailsSchema } from 'pages/NestedCustomers/schema/addCustomersDetailsSchema'
import { getNestedCustomersErrorFormKey } from 'pages/NestedCustomers/utils'
import {
  AddCustomerPayload,
  businessCustomerFields,
  CustomerEntityType,
  individualCustomerFields,
} from 'types/customers'
import { ErrorDetails } from 'types/types'
import { getCountryOptions } from 'utils/forms'
import { CustomerEntityForm } from './CustomerEntityForm'

type AddCustomerFormProps = {
  isSubmitting: boolean
  onBack: () => void
  onContinue: (formData: AddCustomerPayload) => void
  customerFormState: AddCustomerPayload
  formErrors?: ErrorDetails
}

export const AddCustomerForm = ({
  customerFormState,
  onBack,
  onContinue,
  formErrors,
}: AddCustomerFormProps) => {
  const { t } = useTranslation()
  const { accountCapabilities } = useAccountsContext()

  const { data: allowedCountries, isPending: isAllowedCountriesLoading } =
    useAllowedCountriesQuery()

  const allowedCountriesOptions = useMemo(
    () => getCountryOptions(allowedCountries),
    [allowedCountries]
  )

  const { data: countries, isPending: isCountriesLoading } = useCountriesQuery()

  const countriesOptions = useMemo(() => getCountryOptions(countries), [countries])

  const { data: monthlyVolumes, isPending: isFetchingMonthlyVolumes } = useMonthlyVolumesQuery()

  const monthlyVolumesOptions: ComboboxItem[] = monthlyVolumes?.map(data => ({
    value: data.reference,
    leftText: data.name,
  }))

  const { industryOptions, isPending: isFetchingIndustries } = useIndustryOptions()

  const formSchema = addCustomerDetailsSchema(t)
  const { control, formState, register, setValue, resetField, trigger, watch, setError } =
    useForm<AddCustomerPayload>({
      resolver: yupResolver(formSchema),
    })
  const formValues = watch()

  const [
    // General
    customerEntityType,
    externalReference,
    countryCode,
    stateCode,
    addressLine1,
    addressLine2,
    city,
    postalCode,
    // Business
    businessName,
    industry,
    businessDescription,
    registrationNumber,
    businessMonthlyVolume,

    businessTaxResidency,

    nationality,
    nationalityName,
    // Individual
    firstName,
    lastName,
    dateOfBirth,
    personalIdNumber,
    individualCustomerDescription,
  ] = useWatch({
    control,
    name: [
      // General
      'customerEntityType',
      'externalReference',
      'countryCode',
      'stateCode',
      'addressLine1',
      'addressLine2',
      'city',
      'postalCode',
      // Business
      'businessName',
      'industry',
      'businessDescription',
      'registrationNumber',
      'businessMonthlyVolume',
      'businessTaxResidency',

      'nationality',
      'nationalityName',
      // Individual
      'firstName',
      'lastName',
      'dateOfBirth',
      'personalIdNumber',
      'individualCustomerDescription',
    ],
    defaultValue: {
      // General
      customerEntityType: customerFormState?.customerEntityType ?? CustomerEntityType.COMPANY,
      customerType: customerFormState?.customerEntityType ?? CustomerEntityType.COMPANY,
      externalReference: '',
      countryCode: '',
      countryName: '',
      stateCode: '',
      stateName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postalCode: '',
      // Business
      businessName: '',
      industry: '',
      industryName: '',
      businessDescription: '',
      registrationNumber: '',
      businessMonthlyVolume: '',
      businessMonthlyVolumeName: '',
      businessTaxResidency: '',
      businessTaxResidencyName: '',
      nationality: '',
      nationalityName: '',
      // Individual
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      personalIdNumber: '',
      individualCustomerDescription: '',
    },
  })

  const { data: states, isFetching: isFetchingStates } = useCountryStatesQuery(
    formValues?.countryCode,
    formValues?.countryCode === 'US'
  )

  const stateComboboxItems =
    states?.map(state => ({
      value: state.value,
      leftText: state.leftText,
    })) || []

  const [activeTab, setActiveTab] = useState<CustomerEntityType>(
    customerFormState?.customerEntityType ?? CustomerEntityType.COMPANY
  )

  useEffect(() => {
    setCachedValues()
  }, [onBack])

  useEffect(() => {
    if (formErrors && !!Object.keys(formErrors).length) {
      setFieldErrors(formErrors, getNestedCustomersErrorFormKey, setError)
    }
  }, [formErrors, setError])

  const setCachedValues = () => {
    if (!customerFormState) {
      return
    }
    Object.keys(customerFormState).forEach((field: keyof AddCustomerPayload) => {
      if (customerFormState[field] && !!Object.keys(customerFormState)?.length) {
        setValue(field, customerFormState[field])
      }
    })
  }

  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const handleContinue = async () => {
    const isFieldsValid = await trigger(
      customerEntityType === CustomerEntityType.COMPANY
        ? businessCustomerFields
        : individualCustomerFields
    )

    if (isFieldsValid) {
      onContinue(formValues)
    }
    if (!isFieldsValid) {
      // Trigger scroll to the first error immediately on invalid
      const formLike = { formState } as { formState: typeof formState }
      scrollToTopError(formLike, fieldRefs)
    }
  }

  useEffect(() => {
    setValue(
      'customerEntityType',
      customerFormState?.customerEntityType ?? CustomerEntityType.COMPANY
    )
  }, [])

  const resetFields = (newSelectedCustomerType: CustomerEntityType) => {
    switch (newSelectedCustomerType) {
      case CustomerEntityType.COMPANY:
        individualCustomerFields?.forEach(field => {
          resetField(field)
        })
        return
      case CustomerEntityType.INDIVIDUAL:
        businessCustomerFields?.forEach(field => {
          resetField(field)
        })
        return
      default:
        return
    }
  }

  const onEntityTypeChange = (value: CustomerEntityType) => {
    resetFields(value)
    setValue('customerEntityType', value)
    setActiveTab(value)
    setValue('nationality', '')
    setValue('nationalityName', '')
  }

  return (
    <Box id="add-customer-form" flexCol width="full" paddingX={12} paddingT={24} gapY={20}>
      {accountCapabilities?.isPrincipleCustomerIndividualCapabilityEnabled && (
        <Radio
          {...register('customerEntityType')}
          key={formValues?.customerEntityType}
          onChange={onEntityTypeChange}
          value={formValues?.customerEntityType}
          horizontal
          options={[
            { value: CustomerEntityType.COMPANY, label: t('customers.business') },
            {
              value: CustomerEntityType.INDIVIDUAL,
              label: t('customers.individual'),
            },
          ]}
        />
      )}
      <Box ref={el => (fieldRefs.current.externalReference = el)}>
        <Input
          {...register('externalReference')}
          label={t('customers.externalReference')}
          optionalText={t('optional')}
          name="externalReference"
          testId="external-reference"
          inputMode="text"
          value={externalReference}
          onChange={e => setValue('externalReference', e?.target?.value)}
          errorText={formState?.errors?.externalReference?.message}
          error={!!formState?.errors?.externalReference}
          Tooltip={<Tooltip type="complex" bodyContent={t('customers.externalReferenceTooltip')} />}
        />
      </Box>
      <CustomerEntityForm
        activeTab={activeTab}
        businessName={businessName}
        businessDescription={businessDescription}
        registrationNumber={registrationNumber}
        isCountriesLoading={isCountriesLoading}
        countries={countriesOptions}
        isAllowedCountriesLoading={isAllowedCountriesLoading}
        allowedCountries={allowedCountriesOptions}
        businessTaxResidency={businessTaxResidency}
        businessMonthlyVolume={businessMonthlyVolume}
        monthlyVolumesOptions={monthlyVolumesOptions}
        isFetchingMonthlyVolumes={isFetchingMonthlyVolumes}
        industryOptions={industryOptions}
        isFetchingIndustries={isFetchingIndustries}
        states={stateComboboxItems}
        isFetchingStates={isFetchingStates}
        addressLine1={addressLine1}
        addressLine2={addressLine2}
        city={city}
        postalCode={postalCode}
        countryCode={countryCode}
        stateCode={stateCode}
        industry={industry}
        firstName={firstName}
        lastName={lastName}
        dateOfBirth={dateOfBirth}
        nationality={nationality}
        personalIdNumber={personalIdNumber}
        nationalityName={nationalityName}
        individualCustomerDescription={individualCustomerDescription}
        errors={formState?.errors}
        register={register}
        setValue={setValue}
      />
      <Box flex justify="between" paddingT={20}>
        <Button variant="secondary" onClick={onBack}>
          {t('cancel')}
        </Button>
        <Button onClick={handleContinue}>{t('continue')}</Button>
      </Box>
    </Box>
  )
}
